// frontend/src/components/legal/PrivacyPolicy.js
import React from 'react';

const LAST_UPDATED = 'September 2026';

function Section({ title, children }) {
  return (
    <section style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 8, color: '#111827' }}>{title}</h2>
      <div style={{ fontSize: 14, lineHeight: 1.75, color: '#374151' }}>{children}</div>
    </section>
  );
}

export default function PrivacyPolicy() {
  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '40px 20px 80px' }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, margin: '0 0 4px', color: '#111827' }}>Privacy Policy</h1>
        <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 32 }}>Last updated: {LAST_UPDATED}</p>

        <Section title="1. What this policy covers">
          <p>This policy covers Wavelines ERP: the business-management application used by business owners and their authorized staff. Wavelines Federation and Wavelines Shop are separate services with their own data responsibilities. When an ERP business enables marketplace publishing, only the listing information described below is sent downstream to the Federation.</p>
        </Section>

        <Section title="2. What Wavelines ERP collects">
          <ul style={{ paddingLeft: 20 }}>
            <li><strong>Account data:</strong> name and email used for Google sign-in, plus the role and business access assigned to that account.</li>
            <li><strong>Business data:</strong> information entered to operate the business, including inventory, products, orders, sales, payments, expenses, staff records, customers, shops, assets, and related operational records.</li>
            <li><strong>Marketplace publishing data:</strong> when a business enables marketplace publishing, eligible product/listing information and the marketplace profile needed to present that business publicly may be sent to the independent Wavelines Federation service.</li>
            <li><strong>Technical and security data:</strong> IP addresses, timestamps, request/error logs, and similar operational signals used for authentication, rate limiting, abuse prevention, reliability, and debugging.</li>
          </ul>
        </Section>

        <Section title="3. Service providers">
          <p>Depending on the features configured or used, Wavelines ERP relies on service providers for specific infrastructure functions:</p>
          <ul style={{ paddingLeft: 20 }}>
            <li><strong>Google</strong> — Google OAuth sign-in.</li>
            <li><strong>Neon / PostgreSQL infrastructure</strong> — the ERP's authoritative application database.</li>
            <li><strong>Render and Vercel</strong> — backend and frontend application hosting.</li>
            <li><strong>Cloudinary</strong> — media hosting for ERP product images and business branding while that integration is enabled.</li>
            <li><strong>SendByte</strong> — transactional account and administrative email delivery.</li>
            <li><strong>Cloudflare</strong> — configured DNS, email routing, edge, and security services.</li>
            <li><strong>Groq</strong> — AI-assisted business analysis where that feature is used.</li>
          </ul>
          <p style={{ marginTop: 10 }}>Each provider receives only the information needed for the relevant feature or infrastructure function.</p>
        </Section>

        <Section title="4. How ERP data is used">
          <p>ERP data is used to provide the business-management features requested by the business, authenticate users, enforce permissions and plan limits, maintain security and reliability, provide requested AI features, deliver transactional account messages, and publish marketplace listings when the business has enabled that feature.</p>
          <p>Wavelines ERP does not sell business data. Business data is not intentionally submitted to an AI provider except when an ERP feature requiring that provider is invoked.</p>
        </Section>

        <Section title="5. Marketplace publishing">
          <p>Marketplace publishing is optional. When enabled, eligible public listing fields can be projected from ERP to the independent Wavelines Federation service for use by the shared marketplace. ERP remains authoritative for its local business records. Private ERP records such as internal financial records, staff permissions, and non-listing operational data are not part of that public projection.</p>
        </Section>

        <Section title="6. Backups and exports">
          <p>Backup availability depends on the business's active plan and the backup features currently configured for that plan. Businesses should keep independent exports of information they consider critical. The application shows the plan features available to the signed-in business.</p>
        </Section>

        <Section title="7. Data retention and deletion">
          <p>Business data is retained while needed to provide the ERP and meet applicable operational or legal requirements. Authorized users can use the ERP's export and deletion-request workflows where available. Approved deletion requests remove the covered ERP business data according to the application's deletion process.</p>
        </Section>

        <Section title="8. Your rights">
          <p>Depending on applicable law, users may have rights to access, correct, export, or request deletion of personal data and to object to certain processing. Nigerian users may have rights under the Nigeria Data Protection Act 2023. Requests can be made through the support or administrative contact provided for the service.</p>
        </Section>

        <Section title="9. Security">
          <p>Wavelines ERP uses HTTPS, authenticated sessions, role-based authorization, tenant-scoped data access, rate limiting, audit logging, and other operational safeguards. No online service can guarantee absolute security, so access credentials and exported business data should also be handled carefully by each business.</p>
        </Section>

        <Section title="10. Payments and billing">
          <p>ERP subscription upgrades are currently handled through the application's bank-transfer and administrative approval workflow. Wavelines ERP does not ask users to provide card credentials or online-banking passwords through the ERP.</p>
        </Section>

        <Section title="11. Changes to this policy">
          <p>This policy may be updated when the ERP's data flows, providers, or features change. Material changes should be reflected here with a new last-updated date.</p>
        </Section>

        <Section title="12. Contact">
          <p>Privacy, access, export, correction, or deletion questions can be sent through the support or administrative contact provided for Wavelines ERP.</p>
        </Section>

        <div style={{ marginTop: 40, padding: 16, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, fontSize: 12.5, color: '#92400e', lineHeight: 1.6 }}>
          This policy describes the current ERP implementation and should be reviewed by a qualified privacy professional before being treated as final legal advice.
        </div>
      </div>
    </div>
  );
}
